import { useState } from "react";
import type { ActionItem } from "@/types/analysis";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Filter, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";

const impactColor = { low: "bg-info/20 text-info", medium: "bg-warning/20 text-warning", high: "bg-destructive/20 text-destructive" };
const difficultyColor = { low: "bg-success/20 text-success", medium: "bg-warning/20 text-warning", high: "bg-destructive/20 text-destructive" };
const categoryLabel = { seo: "SEO", "ai-visibility": "AI Visibility", authority: "Authority", technical: "Technical" };
const categoryColor = { seo: "bg-primary/20 text-primary", "ai-visibility": "bg-accent/20 text-accent", authority: "bg-info/20 text-info", technical: "bg-warning/20 text-warning" };

export const ActionPlanTab = ({ data }: { data: ActionItem[] }) => {
  const [filterCat, setFilterCat] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("impact");
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());

  const toggleCompleted = (id: string) => {
    setCompletedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = data.filter((a) => filterCat === "all" || a.category === filterCat);
  const sorted = [...filtered].sort((a, b) => {
    const aCompleted = completedIds.has(a.id) ? 1 : 0;
    const bCompleted = completedIds.has(b.id) ? 1 : 0;
    if (aCompleted !== bCompleted) return aCompleted - bCompleted;
    const impactOrder = { high: 3, medium: 2, low: 1 };
    if (sortBy === "impact") return impactOrder[b.impact] - impactOrder[a.impact];
    if (sortBy === "difficulty") return impactOrder[a.difficulty] - impactOrder[b.difficulty];
    return 0;
  });

  const completedCount = data.filter((a) => completedIds.has(a.id)).length;
  const progressPercent = data.length > 0 ? (completedCount / data.length) * 100 : 0;

  return (
    <div className="space-y-4 mt-4">
      {/* Progress summary */}
      <Card className="border-border bg-card">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <span className="text-sm font-medium">{completedCount} of {data.length} completed</span>
            </div>
            <span className="text-xs text-muted-foreground">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="seo">SEO</SelectItem>
              <SelectItem value="ai-visibility">AI Visibility</SelectItem>
              <SelectItem value="authority">Authority</SelectItem>
              <SelectItem value="technical">Technical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="impact">Sort by Impact</SelectItem>
            <SelectItem value="difficulty">Sort by Ease</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {sorted.map((action) => (
        <ActionCard
          key={action.id}
          action={action}
          isCompleted={completedIds.has(action.id)}
          onToggle={() => toggleCompleted(action.id)}
        />
      ))}
    </div>
  );
};

const ActionCard = ({ action, isCompleted, onToggle }: { action: ActionItem; isCompleted: boolean; onToggle: () => void }) => {
  const [open, setOpen] = useState(false);

  return (
    <Card className={`border-border bg-card transition-opacity ${isCompleted ? "opacity-60" : ""}`}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors">
            <div className="flex items-start gap-3">
              <div
                className="pt-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
              >
                <Checkbox checked={isCompleted} />
              </div>
              <div className="flex-1 min-w-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium w-fit ${categoryColor[action.category]}`}>
                  {categoryLabel[action.category]}
                </span>
                <div className="flex items-start justify-between gap-3 mt-1">
                  <div className="flex-1">
                    <CardTitle className={`text-sm leading-tight ${isCompleted ? "line-through text-muted-foreground" : ""}`}>{action.title}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${impactColor[action.impact]}`}>
                      Impact: {action.impact}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${difficultyColor[action.difficulty]}`}>
                      Effort: {action.difficulty}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Evidence</div>
              <p className="text-xs text-foreground bg-secondary/30 p-2 rounded">{action.evidence}</p>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Steps</div>
              <ol className="space-y-1">
                {action.steps.map((s, i) => (
                  <li key={i} className="text-xs text-foreground flex gap-2">
                    <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">{i + 1}</Badge>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
            {action.contentSuggestion && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Content Suggestion</div>
                <p className="text-xs text-foreground bg-primary/5 p-2 rounded border border-primary/10">{action.contentSuggestion}</p>
              </div>
            )}
            {action.codeSuggestion && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Code Example</div>
                <pre className="text-xs text-foreground bg-background p-2 rounded font-mono overflow-x-auto">{action.codeSuggestion}</pre>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
